import React from "react";
import AppSwitch from "components/Shared/AppSwitch";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import { useListGlobalListQuery } from "store/services/globalListApi";
import { useGetContractorUserDataDetailQuery } from "store/services/contractorUserApi";
import { GLOBAL_LIST_CONSTANTS } from "data/static/globalListOptions";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";

import { useAuth } from "hooks";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";

const INITIAL_VALUE = {
  name: "",
  driverId: "",
  ownerName: "",
  assigned_vehicle_ids: [],
  isActive: true,
  medicalCertificationStatus: null,
  account: [],
  entity: [],
  email: "",
  phone: "+977--NP", // Initialize with static country values
  ownerphone: "",
  bankAccountDetails: {
    bankName: "",
    accountName: "",
    accountNumber: "",
  },
  contractorProfile: null,
};

const DriverForm = ({ driver = null, isBusy = false, onSubmit = () => { } }) => {
  const navigate = useNavigate();
  const {
    viewEntity,
    isAccountAdmin,
    isSystemAdmin,
    isContractor,
    loggedInUser,
  } = useAuth();

  // React form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      ...INITIAL_VALUE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
      phone: driver?.phone || "+977--NP", // Initialize with static country values
      ownerphone: driver?.ownerphone || "",
      ownerName: driver?.ownerName || "",
      bankAccountDetails: {
        bankName: driver?.bankAccountDetails?.bankName || "",
        accountName: driver?.bankAccountDetails?.accountName || "",
        accountNumber: driver?.bankAccountDetails?.accountNumber || "",
      },
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });

  // RTK Query for account
  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities = [], isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  // RTK Query for fetching vehicles
  const [
    fetchVehicles,
    { data: assigned_vehicle_ids, isLoading: loadingVehicles },
  ] = useLazyGetDDLVehicleListQuery();

  const {
    data: MedicalCertificateOptions,
    isLoading: loadingMedicalCertificateOptions,
  } = useListGlobalListQuery({
    type: GLOBAL_LIST_CONSTANTS.MEDICAL_CERTIFICATE,
    isSubType: false,
    includeChildren: true,
    status: "Active",
  });

  // Contractor profile query (only for contractors)
  const { data: contractorData, isLoading: loadingContractorData } =
    useGetContractorUserDataDetailQuery(loggedInUser?.typeRef, {
      skip: !isContractor || !loggedInUser?.typeRef,
    });

  // Methods
  const handleSubmitDriver = (data) => {
    const _driverPayload = {
      ...data,
      // ownerName/ownerphone are independent of name/phone — only send
      // what the user actually typed in those fields. Don't silently
      // fall back to name/phone (matches the form's display behaviour).
      ownerName: data.ownerName || "",
      ownerphone: data.ownerphone || "",
      bankAccountDetails: {
        bankName: data.bankAccountDetails?.bankName || "",
        accountName: data.bankAccountDetails?.accountName || "",
        accountNumber: data.bankAccountDetails?.accountNumber || "",
      },
      entity: data?.entity?._id,
      account: data?.account?._id,
      contractorId: isContractor ? data?.contractorProfile?._id : undefined,
      assigned_vehicle_ids:
        data?.assigned_vehicle_ids?.map((vehicle) => vehicle._id) || [],
      medicalCertificationStatus: data.medicalCertificationStatus || null,
    };

    onSubmit(_driverPayload);
  };

  React.useEffect(() => {
    if (!driver) return;
    reset({
      ...driver,
      ownerphone: driver.ownerphone || "",
      ownerName: driver.ownerName || "",
      bankAccountDetails: {
        bankName: driver.bankAccountDetails?.bankName || "",
        accountName: driver.bankAccountDetails?.accountName || "",
        accountNumber: driver.bankAccountDetails?.accountNumber || "",
      },
      contractorProfile: driver.contractorId
        ? { _id: driver.contractorId }
        : null,
      medicalCertificationStatus:
        driver.medicalCertificationStatus?._id || null,
      phone: driver.phone || "+977--NP", // Initialize with static country values
    });
  }, [driver, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchVehicles({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchVehicles, viewEntity]);

  React.useEffect(() => {
    if (!driver && MedicalCertificateOptions?.length > 1) {
      setValue("medicalCertificationStatus", MedicalCertificateOptions[0]._id || null);
    }
  }, [setValue, driver, MedicalCertificateOptions]);
  return (
    <AppForm loading={isBusy || loadingMedicalCertificateOptions}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && !loadingAccounts && accounts && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                    }}
                    options={accounts || []}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && !loadingEntities && entities && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {isContractor && (
            <Grid item md={6}>
              <Controller
                name="contractorProfile"
                control={control}
                rules={{ required: "Please select a contractor profile." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Contractor Profile"
                    value={value || ""}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={contractorData?.contractorProfiles || []}
                    loading={loadingContractorData}
                    loadingText="Loading Contractor Profiles..."
                    helperText={errors?.contractorProfile?.message}
                    error={!!errors?.contractorProfile}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Please provide a name." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Name"
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="email"
              control={control}
              rules={{ required: "Enter your email." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Email"
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone is required",

                validate: (value) => {
                  const digits = value.replace(/\D/g, "");
                  if (digits.length < 7 || digits.length > 17) {
                    return "Phone number must be between 9 and 16 digits";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <CustomCountryPhoneInput
                  placeholder="Phone Number"
                  label="Phone*"
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="ownerName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Owner Name"
                  helperText="Optional"
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="ownerphone"
              control={control}
              render={({ field }) => (
                <CustomCountryPhoneInput
                  placeholder="Owner Phone"
                  label="Owner Phone"
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="assigned_vehicle_ids"
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple
                  label="Vehicle IDs"
                  value={value || []}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option &&
                    `${option?.vehicle_id} - ${option?.registration_number}`
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt?.vehicle_id === val?.vehicle_id
                  }
                  options={assigned_vehicle_ids || []}
                  loading={loadingVehicles}
                  loadingText="Loading Vehicle IDS..."
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="medicalCertificationStatus"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-certification">
                    Medical Certification
                  </InputLabel>
                  <Select
                    labelId="select-certification"
                    label="Medical Certification"
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value || null)}
                  >
                    {MedicalCertificateOptions?.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            {errors.medicalCertificationStatus && (
              <p className="error">
                {errors.medicalCertificationStatus.message}
              </p>
            )}
          </Grid>
          {driver && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>

        {/* ---------- Bank Account Details (optional) ---------- */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Bank Account Details (Optional)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="bankAccountDetails.bankName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Bank Name"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="bankAccountDetails.accountName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Account Name"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="bankAccountDetails.accountNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Account Number"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </AppForm.Content>

      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handleSubmitDriver)}
        >
          {driver ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default DriverForm;
